import { AUTH_INITIALIZATION_ERROR } from './auth-initialization-error';
import { Router } from '@angular/router';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';
import { tryGetImpersonationData } from './impersonation';
import {
  IMPERSONATION_SESSION_ENDED,
  IMPERSONATION_SESSION_STORAGE_KEY,
} from './constants.impersonation';
import Keycloak from 'keycloak-js';
import { INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG } from 'keycloak-angular';
import { inject, provideAppInitializer, WritableSignal } from '@angular/core';

export function initializeKeycloak(
  keycloak: Keycloak,
  configuration: NimmstaCommonsModuleConfiguration,
  router: Router,
  loginError: WritableSignal<string | null>,
): () => Promise<void> {
  // wrap the initialization in a function to be able to use async/await
  return async () => {
    // The adapter removes callback parameters during init, so capture them first.
    const callbackFragment = window.location.hash.slice(1);
    const stateParams = new URLSearchParams(callbackFragment);
    loginError.set(null);

    try {
      // Try to get the impersonation data
      const impersonationData = await tryGetImpersonationData();

      // If we have impersonation data, set in sessionStorage a property to allow for full-page reloads
      if (impersonationData) {
        sessionStorage.setItem(
          IMPERSONATION_SESSION_STORAGE_KEY,
          JSON.stringify(impersonationData),
        );

        // End the impersonated session directly when the document is unloaded.
        // unfortunately, this also triggers when the user reloads the page, as we can't distinguish between the two
        window.addEventListener('pagehide', (event) => {
          // Explicit logout removes this marker before closing/navigating the tab.
          if (
            !event.persisted &&
            sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY)
          ) {
            sessionStorage.removeItem(IMPERSONATION_SESSION_STORAGE_KEY);
            sessionStorage.setItem(IMPERSONATION_SESSION_ENDED, 'true');

            const sessionId = keycloak.tokenParsed?.['sid'];
            if (
              keycloak.didInitialize &&
              typeof sessionId === 'string' &&
              sessionId &&
              keycloak.token
            ) {
              void fetch(
                `${configuration.keycloakUrl}/realms/${encodeURIComponent(configuration.keycloakRealm)}/account/sessions/${encodeURIComponent(sessionId)}`,
                {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${keycloak.token}` },
                  credentials: 'omit',
                  keepalive: true,
                },
              ).catch(() => {
                // Tab-close cleanup is best effort; the document may already be gone.
              });
            }
          }
        });
      }

      // Parse the state fragment to get the kc_action_status (if present)
      const kcActionStatus = stateParams.get('kc_action_status');
      const kcError = stateParams.get('error');
      if (kcActionStatus !== null) {
        // Set the kc_action_status in sessionStorage to allow other components to react to it
        sessionStorage.setItem('kc_action_status', kcActionStatus);
      }
      if (kcError !== null) {
        // Set the kc_error in sessionStorage to allow other components to react to it
        sessionStorage.setItem('kc_error', kcError);
      }

      // Initialize Keycloak
      await keycloak.init({
        // Let the adapter handle error callbacks without starting another login.
        onLoad: stateParams.get('error')?.trim() ? undefined : 'check-sso',
        checkLoginIframe: false,
        // See https://www.npmjs.com/package/keycloak-angular#setup
        silentCheckSsoRedirectUri: `${window.location.origin}/assets/keycloak/silent-check-sso.html`,

        // If impersonation data is present, use it to login
        token: impersonationData?.access_token,
        refreshToken: impersonationData?.refresh_token,
        idToken: impersonationData?.id_token,
      });
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'error' in error &&
        typeof error.error === 'string' &&
        error.error.trim() !== ''
      ) {
        loginError.set(callbackFragment);
        return;
      }
      console.error('Error initializing Keycloak', error);
      router.navigate(['/500']);
    }
  };
}

export const provideKeycloakInitializer = () =>
  provideAppInitializer(() =>
    initializeKeycloak(
      inject(Keycloak),
      inject(NimmstaCommonsModuleConfiguration),
      inject(Router),
      inject(AUTH_INITIALIZATION_ERROR),
    )(),
  );

export const provideKeycloakBearerInterceptorConfig = (environment: {
  apiUrls: Record<string, string>;
}) => {
  // Collect unique origins from apiUrls (ignoring falsy/invalid values)
  const origins = Array.from(
    new Set(
      Object.entries(environment.apiUrls)
        .map(([, u]) => u)
        .filter((u): u is string => !!u)
        .map((u) => {
          try {
            return new URL(u).origin;
          } catch {
            return null;
          }
        })
        .filter((o): o is string => !!o),
    ),
  );

  // Escape for use in a single RegExp
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sources = origins.map(escapeRegex).join('|');
  const regex = new RegExp(`^(${sources})(\\/.*)?$`, 'i');

  return {
    provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    useValue: [
      {
        urlPattern: regex,
        bearerPrefix: 'Bearer',
      },
    ],
  };
};
